import { FileExtensionType } from '~/enums/FileExtensionType';
import { FileMimeType } from '~/enums/FileMimeType';

/**
 * Downloads a JS File
 * @param file a JS Blob object
 * @param fileName name of file
 */
export function downloadFile(
  file: Blob & { name?: string },
  fileName = 'download',
) {
  const url = URL.createObjectURL(file);

  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener('click', clickHandler);
    }, 150);
  };

  const a = document.createElement('a');
  document.body.append(a);
  a.href = url;
  a.download = file.name || fileName;
  a.addEventListener('click', clickHandler, false);
  a.click();
  a.remove();
}

const KNOWN_FILE_EXTENSIONS: string[] = Object.values(FileExtensionType);

/**
 * Returns the file name without its extension. Removes `.<extension>` when the
 * name ends with it, otherwise removes a trailing known document extension
 * (for example `.docx` on a signed PDF version).
 * @param fileName name of file
 * @param extension extension without the leading dot
 * @returns file name without the extension
 */
export function removeFileNameExtension(
  fileName: string | null | undefined,
  extension?: string | null,
) {
  if (!fileName) return '';
  if (!extension) return fileName;
  const lowerCaseName = fileName.toLowerCase();
  const suffix = [`.${extension}`.toLowerCase(), ...KNOWN_FILE_EXTENSIONS].find(
    (candidate) => lowerCaseName.endsWith(candidate),
  );
  return suffix ? fileName.slice(0, -suffix.length) : fileName;
}

/**
 * Returns a JS File/Blob object from a bytes stream
 * @param bytes standard a bytes streaming
 * @param mimeType standard a enum of file type
 * @returns a JS Blob object
 */
export function bytesToBlob(bytes: BlobPart, mimeType: string) {
  return new Blob([bytes], { type: mimeType });
}

/**
 * Returns file's extension
 * @param fileName file name
 * @returns file type
 */
export function getFileExtension(fileName: string) {
  return `.${fileName.split(/[. ]+/).pop()}`;
}

/**
 * Return a valid and trimmed file type from potentially invalid file type
 * @param fileType
 * @returns trimmed fileType
 */
export function coerceFileType(fileType: unknown) {
  if (typeof fileType !== 'string') {
    return '';
  }
  return fileType.startsWith('.') ? fileType.slice(1) : fileType;
}

/**
 * Returns a valid file Icon key given a File object.
 * Infers the file icon from the file extension or mimetype.
 * @param file
 *    A Javascript File
 * @returns a valid file Icon key
 */
export const getFileIcon = (file: { name?: string; type?: string }) => {
  const { name = 'file', type } = file;
  const extension = getFileExtension(name).toLowerCase();

  switch (extension) {
    case FileExtensionType.Doc:
    case FileExtensionType.Docx:
      return 'file-doc';
    case FileExtensionType.Pdf:
      return 'file-pdf';
    case FileExtensionType.Jpeg:
    case FileExtensionType.Jpg:
    case FileExtensionType.Png:
    case FileExtensionType.Tif:
    case FileExtensionType.Tiff:
    case FileExtensionType.Webp:
      return 'file-image';
    case FileExtensionType.Xlsx:
    case FileExtensionType.Csv:
      return 'file-sheet';
    case FileExtensionType.Ppt:
    case FileExtensionType.Pptm:
    case FileExtensionType.Pptx:
      return 'file-slide';
    default:
      break;
  }

  switch (type) {
    case FileMimeType.Doc:
    case FileMimeType.Docx:
    case FileMimeType.DocUnofficial:
    case FileMimeType.DocxUnofficial:
      return 'file-doc';
    case FileMimeType.Pdf:
      return 'file-pdf';
    case FileMimeType.Csv:
    case FileMimeType.Xlsx:
      return 'file-sheet';
    case FileMimeType.Pptx:
      return 'file-slide';
    default:
      if (typeof type === 'string' && type.includes('image/')) {
        return 'file-image';
      }
      break;
  }

  return 'document';
};

/**
 * Returns a data URL given a Blob or File.
 * @param blob
 *    A Javascript Blob or File.
 * @returns
 *    The data URL.
 */
export const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read the file'));
    reader.readAsDataURL(blob);
  });

/**
 * Formats a file size in bytes to a human-readable string.
 * Returns KB for sizes under 1MB, MB for larger sizes.
 * @param sizeInBytes - The file size in bytes
 * @returns Formatted file size (e.g., "512 KB" or "1.50 MB")
 */
export function formatFileSize(sizeInBytes: number) {
  const sizeInKB = Math.round(sizeInBytes / 1024);
  if (sizeInKB >= 1024) {
    return `${(sizeInKB / 1024).toFixed(2)} MB`;
  }
  return `${sizeInKB} KB`;
}
